import React, { forwardRef, useMemo } from 'react';
import PDFPage from '../PDFPage';
import styles from '../PDFReportPreview.module.css';
import {
    OnOffOption,
    Period,
    periodOptions,
    RevenueType,
    revenueTypeOptions,
    Timeframe,
    timeframeOptions,
} from 'components/RevenueWidgets/helpers';
import { useRevenueContext } from 'contexts/RevenueContext';
import AGTAbleWrapper from 'components/AGTable/AGTableWrapper';
import { getRevenueTableData } from './utils';
import { getLabelFromOptions } from 'components/AGSelect/AGSimpleSelect/AGSimpleSelect';

type RevenuePageProps = {
    spotAddress: string;
    dateTime: string;
    chartCanvas: HTMLCanvasElement | null;
    filters: {
        revenueType: RevenueType;
        timeframe: Timeframe;
        dailyPeriod: Period;
        momYearCompare: OnOffOption;
        momGuaranteeCompare: OnOffOption;
    };
};

const RevenuePage = forwardRef<HTMLDivElement, RevenuePageProps>(function RevenuePage(
    { spotAddress, dateTime, chartCanvas, filters },
    ref
) {
    const { momCurrentYearReport, momPreviousYearReport, dodReport } = useRevenueContext();

    const tableData = useMemo(
        () =>
            getRevenueTableData({
                timeframe: filters.timeframe === Timeframe.MONTHLY ? 'monthly' : 'daily',
                revenueType: filters.revenueType,
                momYearCompare: filters.momYearCompare,
                dailyPeriod: filters.dailyPeriod,
                momCurrentYearReport,
                momPreviousYearReport,
                dodReport,
            }),
        [filters, momCurrentYearReport, momPreviousYearReport, dodReport]
    );

    const filtersInfo = useMemo(() => {
        const filtersForPdf: { label: string; value: string }[] = [
            { label: 'Metric', value: getLabelFromOptions(revenueTypeOptions, filters.revenueType) },
            { label: 'Timeframe', value: getLabelFromOptions(timeframeOptions, filters.timeframe) },
        ];
        if (filters.timeframe === Timeframe.DAILY)
            filtersForPdf.push({ label: 'Period', value: getLabelFromOptions(periodOptions, filters.dailyPeriod) });
        if (filters.timeframe === Timeframe.MONTHLY && filters.momYearCompare === 'on')
            filtersForPdf.push({ label: '', value: 'Compare Year-over-year' });
        if (
            filters.timeframe === Timeframe.MONTHLY &&
            filters.revenueType === RevenueType.NET_REVENUE &&
            filters.momGuaranteeCompare === 'on'
        )
            filtersForPdf.push({ label: '', value: 'Compare to Minimum Guarantee' });
        return filtersForPdf;
    }, [filters]);

    const is30Days = useMemo(
        () => filters.timeframe === Timeframe.DAILY && filters.dailyPeriod === Period.THIRTY,
        [filters]
    );

    return (
        <PDFPage ref={ref} spotAddress={spotAddress} dateTime={dateTime} title="Revenue" filters={filtersInfo}>
            {chartCanvas ? (
                <div className={styles.contentContainer}>
                    <img
                        src={chartCanvas.toDataURL('image/png')}
                        alt="Revenue"
                        className={is30Days ? styles.capturedChartSmall : styles.capturedChart}
                    />

                    {tableData && (
                        <div className={styles.pdfTableContainerCompact}>
                            <AGTAbleWrapper
                                className={is30Days ? styles.firstAndThirdColumnColored : styles.firstColumnColored}
                            >
                                <thead>
                                    <tr>
                                        {tableData.headers.map((header, index) => (
                                            <th key={index}>{header}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {tableData.rows.map((row, rowIndex) => (
                                        <tr key={rowIndex}>
                                            {row.map((cell, cellIndex) => (
                                                <td key={cellIndex}>{cell}</td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </AGTAbleWrapper>
                        </div>
                    )}
                </div>
            ) : (
                <div className={styles.noDataMessage}>
                    <p>No data available for this chart</p>
                </div>
            )}
        </PDFPage>
    );
});

export default RevenuePage;
